import { Severity } from '../../../core/monitoring/instrument';
import isNil from '../../../core/helpers/is-nil';

type CommonErrorFuzzyMatcher = {
  level: Severity;
  message: string;
  matches: (message: string) => boolean;
};

/**
 * List of common global errors, each should have a corresponding documentation to add context information.
 * Matchers must be as precise as possible to avoid false positives.
 *
 * All possible matcher cases should be documented in test cases.
 *
 * Domain specific errors should not be added here, but rather in the respective module.
 */
const commonErrorMatchers: CommonErrorFuzzyMatcher[] = [
  /**
   * This error occurs when a chunk could not be loaded. This happens most often
   * after a re-deployment of the application, where the browser still has
   * the old application instance running and tries to load an old chunk
   * that no longer exists on the server.
   */
  {
    level: Severity.debug,
    message: 'Failed to load chunk, likely due to re-deployment or network issues',
    matches: (message: string) =>
      message.includes('Failed to fetch dynamically imported module:') ||
      message.includes('error loading dynamically imported module:'),
  },
];

type StructuredError = {
  message: string;
  level: Severity;
};

const isErrorLike = (error: unknown): error is { message: string } => {
  return typeof error === 'object' && error !== null && 'message' in error && typeof error.message === 'string';
};

export function detectCommonErrors(error: unknown): StructuredError | null {
  const hasMessage = isErrorLike(error);
  if (!hasMessage) {
    return null;
  }

  const errorMessage = error.message;

  const structuredErrorInfo = commonErrorMatchers.find((matcher) => matcher.matches(errorMessage));

  if (isNil(structuredErrorInfo)) {
    return null;
  }

  return {
    message: structuredErrorInfo.message,
    level: structuredErrorInfo.level,
  };
}
